<script setup>
import { ref, watch, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import WootSubmitButton from 'dashboard/components/buttons/FormSubmitButton.vue';
import { ElInput, ElUpload, ElButton } from 'element-plus';
import { DEFAULT_LANGUAGE_LIST, LANGUAGE_LIST } from '../constants';
import Multiselect from 'vue-multiselect'

// 使用 defineProps 来接收 props
const props = defineProps({
  merchant: Object, // 接收 merchant 对象
  submitData: Function, // 接收父组件传递的 submitData 方法
});
const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { thirdHostURL = '', viteShopPlatformList } = window.chatwootConfig || {};
const actionURL = `${thirdHostURL}/v1/api/chatapp/merchant/file`

const shopName = ref('');
const shopToken = ref('');
const platform = ref('');
const defaultLanguage = ref(null);
const allowImageRecognition = ref(false);
const languageList = ref([]);
const isLoading = ref(false);
const shopWebsite = ref('');
const optionsList = [{'title': t("BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_OPEN_LABEL"), 'value':true},{'title': t("BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_CLOSE_LABEL"), 'value': false},]
const platformList = viteShopPlatformList
// const shopProductWebsite = ref('');

const rules = {
  platform: { required: false},
  shopName: { required: false},
  shopToken: { required: false },
  defaultLanguage: { required },
  allowImageRecognition: { required },
  languageList: { required: false },
  shopWebsite: { required, minLength: minLength(8) },
  // shopProductWebsite: { required, minLength: minLength(12) },
};

const v$ = useVuelidate(rules, {
  shopName,
  shopToken,
  defaultLanguage,
  allowImageRecognition,
  languageList,
  shopWebsite,
  platform,
  // shopProductWebsite
});

const uiFlags = useMapGetter('agents/getUIFlags');

const isDarkMode = computed(() => document.body.classList.contains('dark'));

// 监听 merchant 对象的变化
watch(
  () => props.merchant,
  (newMerchant) => {
    if (newMerchant && Array.isArray(newMerchant.shops)) {
      // 如果 accessTokenList 非空，取第一个元素，否则赋空字符串
      if (newMerchant.shops.length > 0) {
        shopName.value = newMerchant?.shops?.[0]?.shopName;
        shopToken.value = newMerchant?.shops?.[0]?.shopToken;
        defaultLanguage.value = { title: newMerchant?.shops?.[0]?.defaultLanguage, value: newMerchant?.shops?.[0]?.defaultLanguage };
        allowImageRecognition.value = { title: newMerchant?.shops?.[0]?.allowImageRecognition ? t("BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_OPEN_LABEL") : t("BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_CLOSE_LABEL"), value: newMerchant?.shops?.[0]?.allowImageRecognition };
        languageList.value = newMerchant?.shops?.[0]?.languageList;
        shopWebsite.value = newMerchant?.shops?.[0]?.shopWebsite;
        platform.value = { title: newMerchant?.shops?.[0]?.platform, value: newMerchant?.shops?.[0]?.platform };
        // shopProductWebsite.value = newMerchant?.shops?.[0]?.shopProductWebsite;
      }
    }
  },
  { immediate: true } // 初始时就执行一次
);

const writeShop = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (isLoading.value) {
    console.info("重复点击拦截")
    return;
  }
  isLoading.value = true;


  try {
    const shopData = 
        {
          shopName: shopName.value,
          shopToken: shopToken.value,
          defaultLanguage: defaultLanguage.value,
          allowImageRecognition: allowImageRecognition.value,
          languageList: languageList.value || [],
          shopWebsite: shopWebsite.value,
          platform: platform.value,
          // shopProductWebsite: shopProductWebsite.value,
        }
      
      // console.info(shops, "===========================")
    const result = await props.submitData(shopData);
    if (result) {
      useAlert(t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.SAVE_GPT_SHOP_SUCCESS'));
      emit('close');
    }
    isLoading.value = false;
  } catch (error) {
    useAlert(error?.message);
    isLoading.value = false;
  }
};

const onClickToShopifyLogin = () =>  {
  window.open('https://accounts.shopify.com/store-login', '_blank');
};
const onClickToShopifyAccount = () => {
  window.open('https://admin.shopify.com/store/clorisfashion/settings/apps/development', '_blank');
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-visible">
    <woot-modal-header
      :header-title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_INFO_TITLE')"
      :header-content="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_INFO_DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="writeShop">
      <div class="w-full">
        <label :class="{ error: v$.platform.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOP_PLATFORM_LABEL') }}
        </label>
        <multiselect
            style="border: 1px solid #fff; border-radius: 4px;"
            v-model="platform"
            track-by="value"
            label="title"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOP_PLATFORM_LABEL')"
            :options="platformList"
            @input="v$.platform.$touch"
            ></multiselect>
      </div>
      <div class="w-full">
        <label :class="{ error: v$.shopName.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_EDIT_SHOP_NAME_LABEL') }}
          <ElInput
            :class="{ 'custom-input': isDarkMode }"
            :input-style="{ backgroundColor: isDarkMode ? 'rgb(39 42 45)' : '#fff' }"
            v-model="shopName"
            type="text"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_SHOP_NAME_PLACEHOLDERL')"
            @input="v$.shopName.$touch"
          />
          <a class="text-woot-500 dark:text-woot-500 !p-0"  @click="onClickToShopifyLogin">
            {{ $t(`BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_SHOP_NAME_TIPS`) }}
          </a>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.shopToken.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_KEY_LABEL') }}
          <ElInput
            :class="{ 'custom-input': isDarkMode }"
            :input-style="{ backgroundColor: isDarkMode ? 'rgb(39 42 45)' : '#fff' }"
            v-model="shopToken"
            type="password"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_KEY_PLACEHOLDERL')"
            @input="v$.shopToken.$touch"
            show-password
          />
          <a class="text-woot-500 dark:text-woot-500 !p-0"  @click="onClickToShopifyAccount">
            {{ $t(`BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_API_KEY_TIPS`) }}
          </a>
        </label>
      </div>
      
      <div class="w-full">
        <label :class="{ error: v$.allowImageRecognition.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_LABEL') }}
        </label>
        <multiselect
            style="border: 1px solid #fff; border-radius: 4px;"
            v-model="allowImageRecognition"
            track-by="value"
            label="title"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_IMAGE_LABEL')"
            :options="optionsList"
            :preselect-first="true"
            @input="v$.allowImageRecognition.$touch"
            ></multiselect>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.defaultLanguage.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_DEFAULT_LANGUAGE_LABEL') }}
        </label>
        <multiselect
            style="border: 1px solid #fff; border-radius: 4px;"
            v-model="defaultLanguage"
            track-by="value"
            label="title"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_DEFAULT_LANGUAGE_LABEL')"
            :options="DEFAULT_LANGUAGE_LIST"
            @input="v$.defaultLanguage.$touch"
            ></multiselect>
      </div>
      <div class="w-full">
        <label :class="{ error: v$.languageList.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_LANGUAGE_LIST_LABEL') }}
          <multiselect style="border: 1px solid #fff; border-radius: 4px;" v-model="languageList" :options="LANGUAGE_LIST" :multiple="true" :close-on-select="false" :clear-on-select="false" :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_LANGUAGE_LIST_LABEL')" label="title" track-by="value"></multiselect>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.shopWebsite.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_STORE_WEBSITE_LABEL') }}
          <ElInput
            :class="{ 'custom-input': isDarkMode }"
            :input-style="{ backgroundColor: isDarkMode ? 'rgb(39 42 45)' : '#fff' }"
            v-model="shopWebsite"
            type="text"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_STORE_WEBSITE_LABEL')"
            @input="v$.shopWebsite.$touch"
          />
        </label>
      </div>

      <!-- <div class="w-full pt-2">
        <label :class="{ error: v$.shopProductWebsite.$error }">
          {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_STORE_PRODUCT_WEBSITE_LABEL') }}
          <ElInput
            :class="{ 'custom-input': isDarkMode }"
            :input-style="{ backgroundColor: isDarkMode ? 'rgb(39 42 45)' : '#fff' }"
            v-model="shopProductWebsite"
            type="text"
            :placeholder="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SHOPIFY_STORE_PRODUCT_WEBSITE_LABEL')"
            @input="v$.shopProductWebsite.$touch"
          />
        </label>
      </div> -->

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <div class="w-full">
          <WootSubmitButton
            :disabled="v$.$invalid || uiFlags.isCreating"
            :button-text="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.GPT_THIRD_PARTY_SAVE_TITLE')"
            :loading="isLoading"
          />
          <button class="button clear" @click.prevent="emit('close')">
            {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
/* 使用 ::v-deep 穿透 scoped 样式，设置 el-input__inner 的 margin 为 0 */
::v-deep .el-input__wrapper .el-input__inner {
  margin: 0;
  border: none;
  /* background-color: #fff !important; */
}

.custom-input :deep(.el-input__wrapper) {
  background-color: rgb(39 42 45) !important;
}
</style>
